import { useState, useEffect, useCallback } from 'react';
import { 
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  TextInput,
  TextArea,
  Badge,
  toast,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '@etherisc/ui-kit';
import { roleService, type Role, type Permission } from '../../services/roleService';
import { PERMISSION_GROUPS } from '../../constants/roles';
import { 
  ShieldIcon,
  PlusIcon,
  SaveIcon,
  XIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon
} from 'lucide-react';

export function RoleManagement() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  // const [isDeleting, setIsDeleting] = useState(false);


  const showError = useCallback((title: string, description: string) => {
    return toast.error(title, { description });
  }, []);

  const showSuccess = useCallback((title: string, description: string) => {
    return toast.success(title, { description });
  }, []);

  // Load roles and permissions
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [rolesData, permissionsData] = await Promise.all([
          roleService.getRoles(),
          roleService.getPermissions()
        ]);
        
        setRoles(rolesData);
        setPermissions(permissionsData);
        
        // Select first role by default
        if (rolesData.length > 0) {
          setSelectedRole(rolesData[0]);
        }
      } catch (error) {
        console.error('Error loading roles and permissions:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load data';
        showError('Loading Failed', errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [showError]);

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role);
    setIsEditing(false); // Exit edit mode when selecting a different role
    setEditingRole(null); // Clear editing state
  };

  // const handleEditRole = (role: Role, event: React.MouseEvent) => {
  //   event.stopPropagation(); // Prevent role selection
  //   setSelectedRole(role);
  //   setIsEditing(true);
  // };

  const handleSaveRole = async () => {
    if (!editingRole) return;

    try {
      let savedRole: Role;
      
      if (editingRole.id.startsWith('new-role-')) {
        // Create new role
        savedRole = await roleService.createRole({
          name: editingRole.name,
          label: editingRole.label,
          description: editingRole.description,
          hierarchy: editingRole.hierarchy,
          permissions: editingRole.permissions,
        });
        
        // Add to roles list
        setRoles(prev => [...prev, savedRole]);
        showSuccess('Role Created', `Role "${savedRole.label}" created successfully`);
      } else {
        // Update existing role
        savedRole = await roleService.updateRole(editingRole.id, {
          name: editingRole.name,
          label: editingRole.label,
          description: editingRole.description,
          hierarchy: editingRole.hierarchy,
          permissions: editingRole.permissions,
        });
        
        // Update in roles list
        setRoles(prev => prev.map(r => r.id === savedRole.id ? savedRole : r));
        showSuccess('Role Updated', `Role "${savedRole.label}" updated successfully`);
      }
      
      setSelectedRole(savedRole);
      setIsEditing(false);
      setEditingRole(null);
    } catch (error) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save role';
      showError('Save Failed', errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRole(null); // Discard changes
  };

  const updateEditingRole = (field: keyof Role, value: string | number | boolean | string[]) => {
    if (editingRole) {
      setEditingRole({ ...editingRole, [field]: value });
    }
  };

  const togglePermission = (permissionName: string) => {
    if (editingRole) {
      const currentPermissions = editingRole.permissions;
      const newPermissions = currentPermissions.includes(permissionName)
        ? currentPermissions.filter(p => p !== permissionName)
        : [...currentPermissions, permissionName];
      setEditingRole({ ...editingRole, permissions: newPermissions });
    }
  };

  const handleDeleteRole = async (role: Role, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent role selection
    
    const confirmMessage = `Are you sure you want to delete the role "${role.label}"?

This action will:
• Permanently remove the role from the system
• Remove all permission assignments for this role
• Affect users who have this role assigned
• Cannot be undone

Role Details:
• Name: ${role.name}
• Type: ${role.isSystem ? 'System Role' : 'Custom Role'}
• Permissions: ${role.permissions.length}`;

    if (!confirm(confirmMessage)) {
      return;
    }

    // setIsDeleting(true);
    try {
      // TODO: Call roleService.deleteRole(role.id) when backend endpoint exists
      console.log('Deleting role:', role.name);
      
      // Delete role from backend
      await roleService.deleteRole(role.id);
      
      // Remove from roles list
      setRoles(prev => prev.filter(r => r.id !== role.id));
      if (selectedRole?.id === role.id) {
        setSelectedRole(null);
        setIsEditing(false);
        setEditingRole(null);
      }
      showSuccess('Role Deleted', `Role "${role.label}" deleted successfully`);
      
    } catch (error) {
      console.error('Error deleting role:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete role';
      showError('Delete Failed', errorMessage);
    } finally {
      // setIsDeleting(false);
    }
  };

  // const canEditRole = (role: Role): boolean => {
  //   // Administrator role cannot be edited
  //   if (role.name === 'admin') return false;
  //   // User needs roles:write permission
  //   return true; // TODO: Check actual permissions
  // };

  const canDeleteRole = (role: Role): boolean => {
    // Administrator role cannot be deleted
    if (role.name === 'admin') return false;
    // User needs roles:delete permission
    return true; // TODO: Check actual permissions
  };

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandedGroups(new Set());
    } else {
      // Get all groups from both database permissions and navigation permissions
      const dbGroups = Object.keys(getPermissionsByGroup());
      const navGroups = Object.keys(getNavigationPermissions());
      const allGroups = [...new Set([...dbGroups, ...navGroups])];
      setExpandedGroups(new Set(allGroups));
    }
    setExpandAll(!expandAll);
  };

  const getPermissionsByGroup = () => {
    const grouped: Record<string, Permission[]> = {};
    
    // Initialize groups
    Object.keys(PERMISSION_GROUPS).forEach(groupKey => {
      const groupName = groupKey.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
      grouped[groupName] = [];
    });
    
    // Add ungrouped permissions
    grouped['Roles and Permissions'] = [];
    
    // Categorize permissions
    permissions.forEach(permission => {
      let assigned = false;
      
      for (const [groupKey, groupPermissions] of Object.entries(PERMISSION_GROUPS)) {
        if (groupPermissions.includes(permission.name as never)) {
          const groupName = groupKey.replace(/_/g, ' ').toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase());
          grouped[groupName].push(permission);
          assigned = true;
          break;
        }
      }
      
      if (!assigned) {
        grouped['Roles and Permissions'].push(permission);
      }
    });
    
    // Remove empty groups
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0) {
        delete grouped[key];
      }
    });
    
    return grouped;
  };
  
  // Get navigation permissions from PERMISSION_GROUPS (not database)
  // This ensures Navigation tab always shows all nav permissions even if not seeded
  const getNavigationPermissions = () => {
    const navGroups: Record<string, Permission[]> = {};
    
    // Get navigation permission groups from PERMISSION_GROUPS
    const navGroupKeys = Object.keys(PERMISSION_GROUPS).filter(key => 
      key.startsWith('TOP_NAVIGATION') || key.startsWith('NAV_')
    );
    
    navGroupKeys.forEach(groupKey => {
      let groupName = groupKey.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
      
      // Remove "Nav " prefix from section labels
      groupName = groupName.replace(/^Nav /, '');
      
      const permissionNames = PERMISSION_GROUPS[groupKey as keyof typeof PERMISSION_GROUPS];
      
      // Map to Permission objects - always use clean labels for navigation
      navGroups[groupName] = permissionNames.map(permName => {
        const existing = permissions.find(p => p.name === permName);
        
        // Create clean label from permission name (always, even if exists in DB)
        const label = permName
          .replace(/^(topnav|nav):/, '') // Remove prefix
          .replace(/-/g, ' ')             // Replace hyphens with spaces
          .replace(/\b\w/g, l => l.toUpperCase()); // Title case
        
        return {
          id: existing?.id || permName,
          name: permName,
          description: label, // Always use clean label
          resource: 'navigation',
          action: permName.startsWith('topnav:') ? 'topnav' : 'nav'
        };
      });
    });
    
    return navGroups;
  };
  
  // Split permissions into Functions and Navigation tabs
  const splitPermissionsByType = (grouped: Record<string, Permission[]>) => {
    const functions: Record<string, Permission[]> = {};
    
    // Filter out navigation permissions from each group
    Object.entries(grouped).forEach(([groupName, perms]) => {
      // Exclude individual navigation permissions (topnav:* and nav:*)
      const nonNavPerms = perms.filter(p => 
        !p.name.startsWith('topnav:') && !p.name.startsWith('nav:')
      );
      
      // Only add group if it has non-navigation permissions
      if (nonNavPerms.length > 0) {
        functions[groupName] = nonNavPerms;
      }
    });
    
    // Navigation permissions come from PERMISSION_GROUPS directly
    const navigation = getNavigationPermissions();
    
    return { functions, navigation };
  };

  const isPermissionSelected = (permissionName: string): boolean => {
    const roleToCheck = isEditing ? editingRole : selectedRole;
    return roleToCheck?.permissions.includes(permissionName) || false;
  };

  const getCurrentRole = (): Role | null => {
    return isEditing ? editingRole : selectedRole;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading roles and permissions...</div>
      </div>
    );
  }

  const groupedPermissions = getPermissionsByGroup();
  const { functions: functionPermissions, navigation: navigationPermissions } = splitPermissionsByType(groupedPermissions);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ShieldIcon size={24} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-gray-600 mt-1">
            Manage system roles and their associated permissions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 h-[calc(100vh-8rem)]">
        {/* Left Panel: Role List - 30% width */}
        <Card className="lg:col-span-3 flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Roles
              </CardTitle>
              <Button
                size="sm"
                onClick={() => {
                  // Create a new blank role for editing
                  const newRole: Role = {
                    id: 'new-role-' + Date.now(),
                    name: '',
                    label: '',
                    description: '',
                    hierarchy: 10,
                    isSystem: false,
                    permissions: []
                  };
                  setSelectedRole(newRole);
                  setEditingRole({ ...newRole }); // Create copy for editing
                  setIsEditing(true);
                }}
              >
                <PlusIcon size={16} />
                Add Role
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden">
            <div className="space-y-2 h-full overflow-y-auto">
              {roles.map((role) => (
                <div
                  key={role.id}
                  onClick={() => handleRoleSelect(role)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedRole?.id === role.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{role.label}</h3>
                      {role.description && (
                        <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={role.isSystem ? 'secondary' : 'outline'}
                        className={role.isSystem ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-green-100 text-green-800 border-green-200'}
                      >
                        {role.isSystem ? 'System' : 'Custom'}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {role.permissions.length} permissions
                    </span>
                    <span className="text-xs text-gray-500">
                      Level {role.hierarchy}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right Panel: Role Details Form - 70% width */}
        <div className="lg:col-span-7 flex flex-col h-full">
          {selectedRole ? (
            <>
              {/* Role Details */}
              <Card className="flex flex-col flex-1">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Role Details</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          if (selectedRole) {
                            handleDeleteRole(selectedRole, e);
                          }
                        }}
                        disabled={!canDeleteRole(selectedRole)}
                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 disabled:text-gray-400 disabled:border-gray-300"
                      >
                        <TrashIcon size={14} />
                        Delete
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsEditing(true);
                          setEditingRole({ ...selectedRole }); // Create copy for editing
                        }}
                        disabled={selectedRole?.name === 'admin'}
                      >
                        <EditIcon size={14} />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                <div className="flex flex-col h-full">
                  {/* Role Information - Compact layout */}
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <TextInput
                        label="Role Name"
                        value={getCurrentRole()?.name || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEditingRole('name', e.target.value)}
                        disabled={!isEditing || selectedRole?.name === 'admin'}
                        placeholder="e.g., product_admin"
                      />
                      <TextInput
                        label="Display Label"
                        value={getCurrentRole()?.label || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEditingRole('label', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., Product Administrator"
                      />
                      <TextInput
                        label="Hierarchy Level"
                        value={getCurrentRole()?.hierarchy?.toString() || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEditingRole('hierarchy', parseInt(e.target.value) || 0)}
                        disabled={!isEditing}
                        placeholder="0-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <TextArea
                        value={getCurrentRole()?.description || ''}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateEditingRole('description', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Role description..."
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Permissions Section - Takes remaining height */}
                  <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">Permissions</h3>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={toggleExpandAll}
                        >
                          {expandAll ? 'Collapse All' : 'Expand All'}
                        </Button>
                        <Badge variant="outline">
                          {selectedRole.permissions.length} assigned
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Tabbed Permissions: Functions vs Navigation */}
                    <Tabs defaultValue="functions" className="flex flex-col flex-1 min-h-0">
                      <TabsList className="mb-4">
                        <TabsTrigger value="functions">
                          Functions
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {Object.values(functionPermissions).flat().filter(p => isPermissionSelected(p.name)).length}
                          </Badge>
                        </TabsTrigger>
                        <TabsTrigger value="navigation">
                          Navigation
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {Object.values(navigationPermissions).flat().filter(p => isPermissionSelected(p.name)).length}
                          </Badge>
                        </TabsTrigger>
                      </TabsList>
                      
                      {/* Functions Tab */}
                      <TabsContent value="functions" className="flex-1 overflow-y-auto border rounded-lg p-4 mt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                          {/* Left Column: User, Organization, Farmer, Product, Quote, Payment */}
                          <div className="space-y-3">
                            {Object.entries(functionPermissions)
                              .filter(([groupName]) => 
                                ['User Management', 'Organization Management', 'Farmer Management', 'Product Management', 'Quote Management', 'Payment Management'].includes(groupName)
                              )
                              .map(([groupName, groupPermissions]) => (
                            <div key={groupName} className="space-y-2">
                              <button
                                onClick={() => toggleGroup(groupName)}
                                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                              >
                                {expandedGroups.has(groupName) ? (
                                  <ChevronDownIcon size={16} />
                                ) : (
                                  <ChevronRightIcon size={16} />
                                )}
                                {groupName}
                                <Badge variant="secondary" className="text-xs">
                                  {groupPermissions.filter(p => isPermissionSelected(p.name)).length}/
                                  {groupPermissions.length}
                                </Badge>
                              </button>
                              
                              {expandedGroups.has(groupName) && (
                                <div className="ml-6 space-y-1">
                                  {groupPermissions.map((permission) => (
                                    <div key={permission.id} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={permission.id}
                                        checked={isPermissionSelected(permission.name)}
                                        onChange={() => togglePermission(permission.name)}
                                        disabled={!isEditing}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                      />
                                      <label 
                                        htmlFor={permission.id}
                                        className="text-sm text-gray-700 cursor-pointer"
                                      >
                                        <span className="text-sm">
                                          {permission.description || permission.name}
                                        </span>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                          {/* Right Column: Policy, Claims, System, Reporting, Roles, Ledger, Notifications */}
                          <div className="space-y-3">
                            {Object.entries(functionPermissions)
                              .filter(([groupName]) => 
                                !['User Management', 'Organization Management', 'Farmer Management', 'Product Management', 'Quote Management', 'Payment Management'].includes(groupName)
                              )
                              .map(([groupName, groupPermissions]) => (
                            <div key={groupName} className="space-y-2">
                              <button
                                onClick={() => toggleGroup(groupName)}
                                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                              >
                                {expandedGroups.has(groupName) ? (
                                  <ChevronDownIcon size={16} />
                                ) : (
                                  <ChevronRightIcon size={16} />
                                )}
                                {groupName}
                                <Badge variant="secondary" className="text-xs">
                                  {groupPermissions.filter(p => isPermissionSelected(p.name)).length}/
                                  {groupPermissions.length}
                                </Badge>
                              </button>
                              
                              {expandedGroups.has(groupName) && (
                                <div className="ml-6 space-y-1">
                                  {groupPermissions.map((permission) => (
                                    <div key={permission.id} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={`${permission.id}-right`}
                                        checked={isPermissionSelected(permission.name)}
                                        onChange={() => togglePermission(permission.name)}
                                        disabled={!isEditing}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                      />
                                      <label 
                                        htmlFor={`${permission.id}-right`}
                                        className="text-sm text-gray-700 cursor-pointer"
                                      >
                                        <span className="text-sm">
                                          {permission.description || permission.name}
                                        </span>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          </div>
                        </div>
                      </TabsContent>
                      
                      {/* Navigation Tab */}
                      <TabsContent value="navigation" className="flex-1 overflow-y-auto border rounded-lg p-4 mt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                          {/* Left Column: Top Navigation + Dashboards + Clients */}
                          <div className="space-y-3">
                            {Object.entries(navigationPermissions)
                              .filter(([groupName]) => 
                                ['Top Navigation', 'Dashboards', 'Clients Policies', 'Users Organizations'].includes(groupName)
                              )
                              .map(([groupName, groupPermissions]) => (
                              <div key={groupName} className="space-y-2">
                                <button
                                  onClick={() => toggleGroup(groupName)}
                                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                                >
                                  {expandedGroups.has(groupName) ? (
                                    <ChevronDownIcon size={16} />
                                  ) : (
                                    <ChevronRightIcon size={16} />
                                  )}
                                  {groupName}
                                  <Badge variant="secondary" className="text-xs">
                                    {groupPermissions.filter(p => isPermissionSelected(p.name)).length}/
                                    {groupPermissions.length}
                                  </Badge>
                                </button>
                                
                                {expandedGroups.has(groupName) && (
                                  <div className="ml-6 space-y-1">
                                    {groupPermissions.map((permission) => (
                                      <div key={permission.id} className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`${permission.id}-nav`}
                                          checked={isPermissionSelected(permission.name)}
                                          onChange={() => togglePermission(permission.name)}
                                          disabled={!isEditing}
                                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                        />
                                        <label 
                                          htmlFor={`${permission.id}-nav`}
                                          className="text-sm text-gray-700 cursor-pointer"
                                        >
                                          <span className="text-sm">
                                            {permission.description || permission.name}
                                          </span>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          {/* Right Column: Accounting, Products, System, Development */}
                          <div className="space-y-3">
                            {Object.entries(navigationPermissions)
                              .filter(([groupName]) => 
                                !['Top Navigation', 'Dashboards', 'Clients Policies', 'Users Organizations'].includes(groupName)
                              )
                              .map(([groupName, groupPermissions]) => (
                              <div key={groupName} className="space-y-2">
                                <button
                                  onClick={() => toggleGroup(groupName)}
                                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                                >
                                  {expandedGroups.has(groupName) ? (
                                    <ChevronDownIcon size={16} />
                                  ) : (
                                    <ChevronRightIcon size={16} />
                                  )}
                                  {groupName}
                                  <Badge variant="secondary" className="text-xs">
                                    {groupPermissions.filter(p => isPermissionSelected(p.name)).length}/
                                    {groupPermissions.length}
                                  </Badge>
                                </button>
                                
                                {expandedGroups.has(groupName) && (
                                  <div className="ml-6 space-y-1">
                                    {groupPermissions.map((permission) => (
                                      <div key={permission.id} className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`${permission.id}-nav-right`}
                                          checked={isPermissionSelected(permission.name)}
                                          onChange={() => togglePermission(permission.name)}
                                          disabled={!isEditing}
                                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                        />
                                        <label 
                                          htmlFor={`${permission.id}-nav-right`}
                                          className="text-sm text-gray-700 cursor-pointer"
                                        >
                                          <span className="text-sm">
                                            {permission.description || permission.name}
                                          </span>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>

                {/* Action Buttons - Only show when editing */}
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                    >
                      <XIcon size={16} />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveRole}
                    >
                      <SaveIcon size={16} />
                      Save Changes
                    </Button>
                  </div>
                )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="flex flex-col flex-1">
              <CardHeader>
                <CardTitle>Select a Role</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <ShieldIcon size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>Select a role from the list to view its details and permissions</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

    </div>
  );
}
